/**
   DON't USE THIS.  Use <InputField type='number' /> instead
 */
import PropTypes from 'prop-types';
import React from 'react';
import IconButton from 'material-ui/IconButton';
import immutable from 'immutable';
import NavigationArrowDropDown from 'material-ui/svg-icons/navigation/arrow-drop-down';
import NavigationArrowDropUp from 'material-ui/svg-icons/navigation/arrow-drop-up';
import ConstValues from 'utils/const';
import Theme from 'styles/theme';
import 'styles/utils/NumericInputBox.styl';
import classNames from 'classnames';

const commonStyle = immutable.Map({
  height: '24px',
  margin: '0px',
  padding: '0px',
  position: 'absolute',
  width: '24px',
});
const svgIconViewBox = `5 5 14 14`;

class NumericInputBox extends React.Component {
  static propTypes = {
    isNumericValueValid: PropTypes.func.isRequired,
    onTouchTapDownButton: PropTypes.func.isRequired,
    onTouchTapUpButton: PropTypes.func.isRequired,
    onValueUpdate: PropTypes.func.isRequired,
    type: PropTypes.string,
    unit: PropTypes.string,
    unitPosition: PropTypes.string,
    value: PropTypes.number.isRequired,
    precision: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      value: props.value || '',
      unit: null,
      unitPosition: null,
    };
  }

  /**
   * Populate `this.state` with `nextProps` if corresponding keys exist
   * @param  {object} nextProps New properties
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    let state = immutable.Map();
    if (nextProps.value !== null && nextProps.value !== undefined) {
      state = state.set('value', nextProps.value);
    }
    if (nextProps.unit) {
      state = state.set('unit', nextProps.unit);
    }
    if (nextProps.unitPosition) {
      state = state.set('unitPosition', nextProps.unitPosition);
    }
    if (state.count() > 0) {
      this.setState(state.toObject());
    }
  }

  /**
   * Return a style object that's combined from `commonStyle` and the style that's specific for the drop-up button
   * @param  {object} commonStyle A common style object
   * @return {object}             A style object for the drop up button
   */
  dropupButtonStyle(commonStyle) {
    return commonStyle
      .merge({
        marginTop: '5px',
        right: '10px',
      })
      .toObject();
  }

  /**
   * Return a style object that's combined from `commonStyle` and the style that's specific for the drop-down button
   * @param  {object} commonStyle A common style object
   * @return {object}             A style object for the drop down button
   */
  dropdownButtonStyle(commonStyle) {
    return commonStyle
      .merge({
        marginTop: '25px',
        right: '10px',
      })
      .toObject();
  }

  /**
   * Return a string that can be used as `value` to control the <input /> element
   * @return {string} A value string for <input />
   */
  valueString() {
    const state = this.state;
    return this.valueStringToRender(state.value, state.unit, state.unitPosition);
  }

  /**
   * Return a string which is composed from `this.props.value`, `this.props.unit` and
   * `this.props.unit_fix_position` and this string is used directly in the JSX rendered
   * from this component
   * @return {string} A string that's renderable with either a prefix or a postfix
   */
  valueStringToRender(value, unit, position) {
    const precision =
      this.props.precision !== null
        ? this.props.precision
        : ConstValues.numberOfDigitsInNumericSearchFields;
    if (this.state.isEditing) {
      return value;
    } else {
      value = Number(value);
      if (unit) {
        if (position === 'prefix') {
          return `${unit} ${value.toFixed(precision)}`;
        } else if (position === 'postfix') {
          return `${value.toFixed(precision)} ${unit}`;
        }
      }
      if (this.state.value && this.state.value.toFixed) {
        return this.state.value.toFixed(precision);
      }
      return value;
    }
  }

  onBlurInput = () => {
    this.setState({isEditing: false});
    this.props.onValueUpdate(Number(this.state.value), this.props.type);
  };

  /**
   * Callback triggered when the user changes the <input /> element's value by manually typing
   * a string. <NumericInputBox /> should not handle this directly, instead it should pass the new
   * value and its type to the parent <NumericSearchField /> component to handle
   * @param  {object} event A javascript event object
   */
  onChangeNumericInputValue = event => {
    const value = event.currentTarget.value;
    if (this.props.isNumericValueValid(value)) {
      this.setState({value: value});
    }
  };

  /** Select the whole text inside the <input /> element */
  onFocusInput = () => {
    this.setState({isEditing: true});
    setImmediate(() => {
      this.input.select();
    });
  };

  /**
   * Callback triggered when the arrow drop up button was clicked. <NumericInputBox />
   * should not handle this event directly, instead it should call `this.props.onTouchUpButton`
   * so that the parent <NumericSearchField /> can handle it
   */
  onTouchTapArrowUpButton = () => {
    this.props.onTouchTapUpButton(this.state.value, this.props.type);
  };

  /**
   * Callback triggered when the arrow down button was clicked. <NumericInputBox />
   * should not handle this event directly, instead it should call `this.props.onTouchTapDownButton`
   * so that the parent <NumericSearchField /> can handle it
   */
  onTouchTapArrowDownButton = () => {
    this.props.onTouchTapDownButton(this.state.value, this.props.type);
  };

  render() {
    const divClassnames = classNames('numeric-input-box', this.props.className);
    const dropupButtonStyle = this.dropupButtonStyle(commonStyle);
    const dropdownButtonStyle = this.dropdownButtonStyle(commonStyle);
    return (
      <div className={divClassnames}>
        <input
          value={this.valueString()}
          onChange={this.onChangeNumericInputValue}
          disabled={this.props.disabled ? 'disabled' : ''}
          onFocus={this.onFocusInput}
          onBlur={this.onBlurInput}
          ref={c => (this.input = c)}
        />
        {(() => {
          if (this.props.shouldRenderTriangles) {
            return (
              <div>
                <IconButton style={dropupButtonStyle} onClick={this.onTouchTapArrowUpButton}>
                  <NavigationArrowDropUp
                    color={Theme.palette.actionButtonColor}
                    viewBox={svgIconViewBox}
                  />
                </IconButton>
                <IconButton style={dropdownButtonStyle} onClick={this.onTouchTapArrowDownButton}>
                  <NavigationArrowDropDown
                    color={Theme.palette.actionButtonColor}
                    viewBox={svgIconViewBox}
                  />
                </IconButton>
              </div>
            );
          }
        })()}
      </div>
    );
  }
}

export default NumericInputBox;
